import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from "@angular/animations";
import { NG_VALUE_ACCESSOR } from "@angular/forms";
import { SelectMenuOption } from "../select-menu/select-menu.component";
import { SelectMenuAppearance } from "../models/select-menu-apearance";

export const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => MultiSelectMenuComponent),
  multi: true,
};

@Component({
  selector: "multi-select-menu",
  templateUrl: "./multi-select-menu.component.html",
  styleUrls: [
    "../select-menu/select-menu.component.scss",
    "./multi-select-menu.component.scss",
  ],
  animations: [
    trigger("transformMenu", [
      state(
        "void",
        style({
          opacity: 0,
          transform: "scale(0.8)",
        })
      ),
      transition(
        "void => enter",
        animate(
          "120ms cubic-bezier(0, 0, 0.2, 1)",
          style({
            opacity: 1,
            transform: "scale(1)",
          })
        )
      ),
      transition(
        "* => void",
        animate("100ms 25ms linear", style({ opacity: 0 }))
      ),
    ]),
  ],
  providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR],
})

/**
 * This component was copied from the select-menu component and modified to support multiple selections
 */
export class MultiSelectMenuComponent implements OnInit {
  public readonly preventCloseOnDevMode = false;
  public menuOptions: SelectMenuOption[];
  @Input("appearance") public appearance: SelectMenuAppearance = "";
  @Input("length") public length: number;
  @Input("disabled") public disabled: boolean = false;
  @Input("showFilterBox")
  public showFilterBox: boolean = true;
  @ViewChild("componentMain") public componentMain: ElementRef;
  @Output("select-menu-event")
  public menuEvent: EventEmitter<SelectMenuOption[]> = new EventEmitter<
    SelectMenuOption[]
  >();
  public _filteredMenuOptions: SelectMenuOption[];
  public menuIsOpen: boolean = false;
  public filterText: string = "";
  public displayedValue = this.updateDisplayedValue();

  public _defaultOption: SelectMenuOption = {
    text: "בחר מתוך הרשימה",
    key: "select-from-list",
    icon: "",
    customValue: null,
  };

  public selectedOptions: SelectMenuOption[] = [this._defaultOption];

  @Input("defaultOption") set defaultOption(value: SelectMenuOption) {
    this._defaultOption = value || this._defaultOption;
    this._defaultOption.key = "select-from-list";
    this.updateDisplayedValue();
  }

  @Input("menuOptions") public set menuOptionsSetter(
    value: SelectMenuOption[]
  ) {
    this.menuOptions = value;
    this.updateFilteredMenuOptions(value);
  }
  public clearSelection($event: MouseEvent): void {
    $event.stopPropagation();
    $event.preventDefault();
    this.selectedOptions = [];
    this.menuEvent.emit(this.selectedOptions);
    this.updateDisplayedValue();
    this.onTouchedCallback();
    this.onChangeCallback(this.selectedOptions);
  }
  public clickedOption($event: MouseEvent, key: string) {
    this.selectedOptions = this.selectedOptions || [];
    $event.stopPropagation();
    $event.stopImmediatePropagation();
    const optionClicked = this.menuOptions.find((o) => o.key === key);
    if (this.selectedOptions.map((o) => o.key).includes(optionClicked.key)) {
      this.selectedOptions = this.selectedOptions.filter(
        (o) => o.key !== optionClicked.key
      );
    } else {
      this.selectedOptions = this.selectedOptions.concat(optionClicked);
    }

    this.menuEvent.emit(this.selectedOptions);
    this.updateDisplayedValue();
    this.onTouchedCallback();
    this.onChangeCallback(this.selectedOptions);
  }

  public toggleMenu($event) {
    $event.stopPropagation();
    $event.stopImmediatePropagation();
    if (this.disabled) {
      return;
    }
    this.menuIsOpen = !this.menuIsOpen;
    if (!this.menuIsOpen) {
      this.closeMenu();
      return;
    }
    setTimeout(() => {
      // this will make the execution after the above boolean has changed
      this.componentMain.nativeElement.focus();
    }, 10);
  }

  public focusOut($event) {
    $event.stopPropagation();
    $event.stopImmediatePropagation();
    const relatedTarget = $event.relatedTarget as HTMLElement;
    const preventClose =
      this.componentMain.nativeElement.contains(relatedTarget);

    if (preventClose) {
      return;
    }

    this.closeMenu();
  }

  public closeMenu() {
    this.menuIsOpen = false;
  }

  public onTouchedCallback: () => void = () => {};

  public onChangeCallback: (_: any) => void = () => {};

  public ngOnInit(): void {
    this.selectedOptions =
      this.menuOptions && this.menuOptions[0]
        ? [this.menuOptions[0]]
        : this.selectedOptions;
    this.updateDisplayedValue();
  }

  public menuKeyDown($event: KeyboardEvent) {
    if ($event.code === "Delete") {
      $event.stopPropagation();
      $event.stopImmediatePropagation();
      this.selectedOptions = null;
      this.menuEvent.emit(this.selectedOptions);
      this.onTouchedCallback();
      this.onChangeCallback(this.selectedOptions);
      this.closeMenu();
    }
  }

  public isOptionSelected(key: string) {
    return (
      this.selectedOptions &&
      this.selectedOptions.map((o) => o.key).includes(key)
    );
  }

  public clearFilter() {
    this.filterText = "";
    this.updateFilteredMenuOptions(this.menuOptions);
  }

  public filterChanged($event: Event) {
    this.updateFilteredMenuOptions(this.menuOptions);
  }

  protected updateFilteredMenuOptions(menuOptions: SelectMenuOption[]) {
    this._filteredMenuOptions = this.filterText.trim().length
      ? menuOptions.filter((o) => o.text.includes(this.filterText))
      : menuOptions;
  }

  protected updateDisplayedValue() {
    if (this.selectedOptions && this.selectedOptions.length > 1) {
      this.selectedOptions = this.selectedOptions.filter(
        (o) => o.key !== "select-from-list"
      );
    }

    if (
      !this.selectedOptions ||
      !this.selectedOptions.length ||
      (this.selectedOptions.length === 1 &&
        this.selectedOptions[0].key === "select-from-list")
    ) {
      if (!this._defaultOption) {
        return "";
      }
      return (this.displayedValue = this._defaultOption.text);
    }

    return (this.displayedValue = (this.selectedOptions.filter((o) => o) || [])
      .map((o) => o.text)
      .join(", "));
  }

  protected registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  //From ControlValueAccessor interface
  protected registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  protected writeValue(value: SelectMenuOption[]) {
    this.selectedOptions = value || [];
    this.updateDisplayedValue();
  }
}



// WEBPACK FOOTER //
// ./src/app/form/multi-select-menu/multi-select-menu.component.ts